import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useNavigate } from 'react-router-dom';

import { deleteTask } from 'api/task.service';
import { useToast } from 'hooks/ui';
import { Task } from 'models/task.model';
import { taskRoutes } from 'router/taskRoutes';
import { useApplicationStore } from 'store/application';
import { resetDraft, setDraftTask, useTaskStore } from 'store/task';
import { taskKeys } from './queryKeys';

export const useTaskDeleteQuery = () => {
  const { tasks } = useTaskStore((state) => state);
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { setLoading, removeLoading } = useApplicationStore();

  const discardDraft = (): void => {
    resetDraft();

    if (tasks.length) {
      setDraftTask({ task: tasks[0] });
      navigate(taskRoutes.taskId(tasks[0]?.id));

      return;
    }

    navigate(taskRoutes.base);
  };

  const { isPending, isError, mutate } = useMutation({
    mutationFn: (id: string) => {
      setLoading();

      return deleteTask(id);
    },
    onSuccess: ({ id, taskGroupId }: Task) => {
      queryClient.invalidateQueries({ queryKey: taskKeys.all });
      toast.success('Task has been deleted successfully');

      const tasksInTheSameGroup = tasks.filter(
        (task) => task.id !== id && task.taskGroupId === taskGroupId,
      );

      const lastTask = tasksInTheSameGroup.at(-1);

      if (lastTask) {
        navigate(taskRoutes.taskId(lastTask.id));
        setDraftTask({ task: lastTask });
      } else {
        navigate(taskRoutes.notFound);
        resetDraft();
      }
    },
    onError: (error: AxiosError) => toast.error(error.message),
    onSettled: () => removeLoading(),
  });

  return { isLoading: isPending, isError, mutate, discardDraft };
};
